import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  FileText,
  FolderOpen,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { fetchDocuments as fetchDocumentsApi } from "../../services/documentsApi";
import "./Conditions.css";

const DOC_PAGE_SIZE = 6;

const formatFileSize = (bytes) => {
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  }) + " " + date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const DocumentsGrid = ({ loanId, getToken }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState("name-asc");
  const [currentPage, setCurrentPage] = useState(1);

  const fetchDocumentsData = useCallback(async () => {
    if (!loanId) return;
    setLoading(true);
    try {
      const token = await getToken();
      const data = await fetchDocumentsApi(loanId, token);
      setDocuments(data);
    } catch (error) {
      console.error("Failed to load documents:", error);
    } finally {
      setLoading(false);
    }
  }, [loanId, getToken]);

  useEffect(() => {
    if (loanId) {
      fetchDocumentsData();
    }
  }, [loanId, fetchDocumentsData]);

  const sortedDocuments = useMemo(() => {
    const docs = [...documents];
    if (sortBy === "name-asc") docs.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    else if (sortBy === "name-desc") docs.sort((a, b) => (b.name || "").localeCompare(a.name || ""));
    return docs;
  }, [documents, sortBy]);

  const totalPages = Math.ceil(sortedDocuments.length / DOC_PAGE_SIZE);
  const paginatedDocuments = useMemo(() => {
    const start = (currentPage - 1) * DOC_PAGE_SIZE;
    return sortedDocuments.slice(start, start + DOC_PAGE_SIZE);
  }, [sortedDocuments, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [sortBy]);

  return (
    <>
      {/* Documents Filter Bar */}
      <div className="cond-docs-filter-bar">
        <select
          className="cond-filter-select"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
        >
          <option value="name-asc">Sort by: eFolder (A-Z)</option>
          <option value="name-desc">Sort by: eFolder (Z-A)</option>
        </select>
        <button className="cond-refresh-btn" onClick={fetchDocumentsData} disabled={loading}>
          <RefreshCw size={14} className={loading ? "cond-icon-spin" : ""} />
        </button>
      </div>

      {/* Documents List */}
      {loading ? (
        <div className="cond-loading">
          <RefreshCw size={18} className="cond-icon-spin" style={{ marginRight: 8 }} />
          Loading documents...
        </div>
      ) : (
        <div className="cond-list">
          {/* Documents Header */}
          <div className="cond-list-header cond-doc-header">
            <span>eFolder (Documents)</span>
            <span># Files</span>
            <span>Files</span>
            <span>Last Updated</span>
          </div>

          {/* Document Items */}
          {paginatedDocuments.map((doc) => {
            const attachments = doc.attachments || [];
            const fileCount = attachments.length;
            const lastUpdated = attachments.length > 0
              ? attachments.reduce((latest, att) => {
                  const d = new Date(att.createdDate || 0);
                  return d > latest ? d : latest;
                }, new Date(0))
              : null;

            return (
              <div key={doc.id} className="cond-list-item cond-doc-item">
                {/* eFolder Name */}
                <div className="cond-doc-folder">
                  <FolderOpen size={18} className="cond-folder-icon" />
                  <span className="cond-doc-folder-name">{doc.name}</span>
                </div>

                {/* # Files */}
                <div className="cond-doc-file-count">
                  <span>{fileCount}</span>
                </div>

                {/* Files */}
                <div className="cond-doc-files">
                  {fileCount > 0 ? (
                    attachments.map((att) => (
                      <div key={att.id} className="cond-file-item">
                        <FileText size={16} className="cond-file-icon cond-file-icon-pdf" />
                        <div className="cond-file-info">
                          <span className="cond-file-name">{att.name}</span>
                          <span className="cond-file-meta">
                            {formatDate(att.createdDate)} &bull; {formatFileSize(att.fileSize)}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <span className="cond-doc-no-files">No files</span>
                  )}
                </div>

                {/* Last Updated */}
                <div className="cond-doc-last-updated">
                  <span>{lastUpdated ? formatDateTime(lastUpdated.toISOString()) : "\u2014"}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Documents Pagination */}
      {!loading && sortedDocuments.length > 0 && (
        <div className="cond-pagination">
          <span className="cond-pagination-info">
            Showing {(currentPage - 1) * DOC_PAGE_SIZE + 1} to{" "}
            {Math.min(currentPage * DOC_PAGE_SIZE, sortedDocuments.length)} of{" "}
            {sortedDocuments.length} eFolders
          </span>
          <button
            className="cond-page-nav"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            <ChevronLeft size={16} />
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              className={`cond-page-btn ${currentPage === page ? "active" : ""}`}
              onClick={() => setCurrentPage(page)}
            >
              {page}
            </button>
          ))}
          <button
            className="cond-page-nav"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </>
  );
};

export default DocumentsGrid;
